import Vue from 'vue'
import VueRouter from 'vue-router'
import store from 'store'
import isLogged from 'store/modules/auth/isLogged'
import authApi from 'store/modules/auth/api'
import { LOGIN_USER, PROCESS_LOGIN_RESPONSE } from 'store/mutation-types'

Vue.use(VueRouter)
let router
const DESIRED_PATH_KEY = 'user-desired-path'
const privateRoleHook = async (to, from, next) => {
  const toProductId = to.params.productId
  const isLoggedIn = await isLogged()
  if (!isLoggedIn) {
    sessionStorage.setItem(DESIRED_PATH_KEY, to.path)
    next({name: 'login'})
    return
  }

  const isNewCheckout = from.name === 'builder' && to.name === 'builder-create-new'
  if (toProductId && !isNewCheckout) {
    const hasProductAccess = await store.dispatch('auth/SET_CURRENT_PRODUCT', { productId: toProductId })
    if (!hasProductAccess) {
      next({name: 'select-product'})
      return
    }
  }

  next()
}

const roles = {
  private: {
    // TAKE CARE!! beforeEnter wont be called on route changes as it only changes its parameter
    meta: { requiresAuth: true }
  },
  canCreateTemplates: {
    beforeEnter (to, from, next) {
      isLogged().then(() => {
        if (authApi.hasRole('checkout_builder_template')) {
          privateRoleHook(to, from, next)
        } else {
          next('/error')
        }
      })
    }
  }
}

const HealthCheck = () => import(/* webpackChunkName: "HealthCheck" */'./HealthCheck.vue')
const SelectProduct = () => import(/* webpackChunkName: "SelectProduct" */'./modules/Dashboard/components/SelectProduct/SelectProduct.vue')
const Dashboard = () => import(/* webpackChunkName: "Dashboard" */'./modules/Dashboard/Dashboard.vue')
const Builder = () => import(/* webpackChunkName: "Builder" */'./modules/Builder/Builder.vue')
const Templates = () => import(/* webpackChunkName: "Templates" */'./modules/Templates/Templates.vue')
const ErrorPage = () => import(/* webpackChunkName: "Error" */'./modules/Error/Error.vue')
const Wizard = () => import(/* webpackChunkName: "Error" */'./modules/Wizard/Wizard.vue')

const productIdParam = ':productId(\\d+)'
export const routes = [
  {
    path: '/health-check',
    name: 'health-check',
    component: HealthCheck
  },
  {
    path: '/login',
    name: 'login',
    component: () => {
      store.dispatch(`auth/${LOGIN_USER}`)
    }
  },
  {
    path: '/auth/login',
    name: 'login-callback',
    component: () => {
      store.dispatch(`auth/${PROCESS_LOGIN_RESPONSE}`).then(() => {
        const path = sessionStorage.getItem(DESIRED_PATH_KEY)
        router.push(path ? { path } : { name: 'select-product' })
      })
    }
  },
  {
    path: '/auth/renew',
    name: 'login-renew-callback',
    component: () => {
      authApi.processLoginRenewResponse()
    }
  },
  {
    path: '/auth/surrogate',
    name: 'surrogate',
    component: () => {
      authApi.processSurrogateLogin()
      const path = new URLSearchParams(window.location.search).get('redirectTo')
      router.push(path ? { path } : { name: 'select-product' })
    }
  },
  {
    path: '/auth/logout',
    name: 'logout-callback',
    component: () => {
      authApi.processLogoutResponse().then(() => {
        router.push({ name: 'login' })
      })
    }
  },
  {
    path: '/',
    name: 'select-product',
    component: SelectProduct,
    ...roles.private
  },
  {
    path: `/${productIdParam}`,
    name: 'dashboard',
    component: Dashboard,
    ...roles.private
  },
  {
    path: `/${productIdParam}/wizard`,
    name: 'wizard',
    component: Wizard,
    ...roles.private
  },
  {
    path: `/${productIdParam}/new`,
    name: 'dashboard-new',
    component: Dashboard,
    ...roles.private
  },
  {
    path: `/${productIdParam}/templates`,
    name: 'templates',
    component: Templates,
    ...roles.canCreateTemplates
  },
  {
    path: `/${productIdParam}/templates/new`,
    name: 'templates-new',
    component: Builder,
    ...roles.canCreateTemplates
  },
  {
    path: `/${productIdParam}/templates/:templateId(\\d+)`,
    name: 'templates-edit',
    component: Builder,
    ...roles.canCreateTemplates
  },
  {
    path: `/${productIdParam}/builder`,
    name: 'builder',
    component: Builder,
    ...roles.private
  },
  {
    path: `/${productIdParam}/builder/:checkoutId(\\d+)`,
    name: 'builder-create-new',
    component: Builder,
    ...roles.private
  },
  {
    path: `/${productIdParam}/builder/template/:templateId(\\d+)`,
    name: 'builder-create-from-template',
    component: Builder,
    ...roles.private
  },
  {
    path: '*',
    name: 'error',
    component: ErrorPage
  }
]

router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) privateRoleHook(to, from, next)
  else next()
})

export default router
